import { CustomForm, FORM_ITEM_TYPES } from '../types/CustomForm'
import { AnswerState } from './dynamicFormReducer'
import { toSnakeCase } from './stringUtils'

/** Legacy payload format: { form: { <sectionKey>: [{ id: itemId, value: answer }] } } */
export interface SubmissionPayload {
  form: Record<string, Array<{ id: string; value: unknown }>>
}

/** New payload format: { form: { <sectionKey>: { [itemId]: answer } } } */
export interface SubmissionPayloadV2 {
  form: Record<string, Record<string, unknown>>
}

/**
 * 🔄 isNumericType: Determines if a form item type is numeric
 * @param typeId - The form item type ID
 * @returns true if the type expects numeric values
 */
const isNumericType = (typeId: number): boolean => {
  return typeId === FORM_ITEM_TYPES.NUMBER_FIELD
}

/**
 * 🔄 castNumericValue: Safely casts a value to number for numeric fields
 * @param value - The raw value from form answers
 * @param typeId - The form item type ID
 * @returns Properly cast value (number, null, or original value)
 */
const castNumericValue = (value: unknown, typeId: number): unknown => {
  if (!isNumericType(typeId)) {
    return value // Non-numeric types pass through unchanged
  }

  // Handle empty string case for numeric fields
  if (value === '' || value === null || value === undefined) {
    console.log('🔄 castNumericValue: Empty numeric value, returning null', {
      value,
      typeId,
    })
    return null
  }

  // Convert string numbers to actual numbers
  if (typeof value === 'string' && value.trim() !== '') {
    const numericValue = Number(value)
    if (!isNaN(numericValue)) {
      console.log('🔄 castNumericValue: String converted to number', {
        original: value,
        converted: numericValue,
        typeId,
      })
      return numericValue
    }
  }

  // If already a number, return as-is
  if (typeof value === 'number') {
    console.log('🔄 castNumericValue: Already a number, passing through', {
      value,
      typeId,
    })
    return value
  }

  // Fallback for invalid numeric values
  console.log('🔄 castNumericValue: Invalid numeric value, returning null', {
    value,
    typeId,
  })
  return null
}

/**
 * 🔄 isEmptyValue: Determines if a value should be omitted from the payload
 * @param value - The value to check
 * @param typeId - The form item type ID
 * @returns true if the value is considered empty and should be omitted
 */
const isEmptyValue = (value: unknown, typeId: number): boolean => {
  console.log('🔄 isEmptyValue: Checking if value is empty', {
    value,
    typeId,
    valueType: typeof value,
  })

  switch (typeId) {
    case FORM_ITEM_TYPES.TEXT_FIELD:
    case FORM_ITEM_TYPES.EMAIL:
    case FORM_ITEM_TYPES.PHONE:
    case FORM_ITEM_TYPES.URL:
    case FORM_ITEM_TYPES.TEXT_AREA:
    case FORM_ITEM_TYPES.DROPDOWN:
    case FORM_ITEM_TYPES.DATE:
    case FORM_ITEM_TYPES.BIRTH_DATE:
    case FORM_ITEM_TYPES.COUNTRY:
      // Text-based fields: empty if empty string
      return value === ''

    case FORM_ITEM_TYPES.NUMBER_FIELD:
      // Numeric fields: empty if null, undefined, or empty string (but NOT 0)
      return value === null || value === undefined || value === ''

    case FORM_ITEM_TYPES.CHECKBOX:
      // Checkbox (multi-select): empty if empty array or undefined
      return Array.isArray(value) ? value.length === 0 : value === undefined

    default:
      // Generic fallback: empty if undefined, null, or empty string
      return value === undefined || value === null || value === ''
  }
}

/**
 * 🔄 mapFormAnswers: Maps form answers to the array-per-section payload format, omitting empty values
 * @param form - The CustomForm definition with groups and items
 * @param answers - The AnswerState containing user responses
 * @returns SubmissionPayload - The array-per-section payload structure for backend submission (only non-empty values)
 */
export const mapFormAnswers = (
  form: CustomForm,
  answers: AnswerState
): SubmissionPayload => {
  console.log(
    '🔄 mapFormAnswers: Mapping form answers to array-per-section payload (omitting empty values)',
    {
      formId: form.id,
      answersCount: Object.keys(answers).length,
    }
  )

  const nested: Record<string, Array<{ id: string; value: unknown }>> = {}

  // Iterate through every item in every group
  form.groups.forEach((group) => {
    const sectionKey = toSnakeCase(group.name) // e.g. "Personal Info" → "personal_info"

    group.items.forEach((item) => {
      const userAnswer = answers[item.id]
      const typeId = item.form_item_type.id

      // ------- NEW: normalise checkbox defaults -------
      const hasNoUserAnswer = userAnswer === undefined || userAnswer === null
      const normalisedAnswer =
        hasNoUserAnswer && typeId === FORM_ITEM_TYPES.CHECKBOX
          ? false
          : userAnswer
      // -------------------------------------------------

      if (hasNoUserAnswer && typeId !== FORM_ITEM_TYPES.CHECKBOX) {
        // unchanged skip logic for non-checkboxes
        console.log(
          '🔄 mapFormAnswers: Skipping item - no user answer provided',
          {
            itemId: item.id,
            itemType: typeId,
            sectionKey,
          }
        )
        return
      }

      // Cast the value if needed (primarily for numeric fields)
      const castedValue = castNumericValue(normalisedAnswer, typeId)

      // Check if the final value is empty and should be omitted
      if (isEmptyValue(castedValue, typeId)) {
        console.log('🔄 mapFormAnswers: Skipping item - value is empty', {
          itemId: item.id,
          itemType: typeId,
          sectionKey,
          userAnswer: normalisedAnswer,
          castedValue,
        })
        return
      }

      // Initialize section array if it doesn't exist
      if (!nested[sectionKey]) {
        nested[sectionKey] = []
      }

      // Include the non-empty value in the array payload
      nested[sectionKey].push({ id: item.id, value: castedValue })

      console.log('🔄 mapFormAnswers: Included item in array payload', {
        itemId: item.id,
        itemType: typeId,
        sectionKey,
        isNumeric: isNumericType(typeId),
        userAnswer: normalisedAnswer,
        finalValue: castedValue,
        finalType: typeof castedValue,
      })
    })
  })

  console.log(
    '🔄 mapFormAnswers: Array-per-section payload created (empty values omitted)',
    {
      sectionCount: Object.keys(nested).length,
      nested,
    }
  )

  return { form: nested }
}

/**
 * 🔄 mapFormAnswersObject: Maps form answers to the key-value payload format, omitting empty values
 * @param form - The CustomForm definition with groups and items
 * @param answers - The AnswerState containing user responses
 * @returns SubmissionPayloadV2 - The key-value payload structure for backend submission (only non-empty values)
 */
export const mapFormAnswersObject = (
  form: CustomForm,
  answers: AnswerState
): SubmissionPayloadV2 => {
  console.log(
    '🔄 mapFormAnswersObject: Mapping form answers to key-value payload (omitting empty values)',
    {
      formId: form.id,
      answersCount: Object.keys(answers).length,
    }
  )

  const nested: Record<string, Record<string, unknown>> = {}

  // Iterate through every item in every group
  form.groups.forEach((group) => {
    const sectionKey = toSnakeCase(group.name) // e.g. "Personal Info" → "personal_info"

    group.items.forEach((item) => {
      const userAnswer = answers[item.id]
      const typeId = item.form_item_type.id

      // ------- NEW: normalise checkbox defaults -------
      const hasNoUserAnswer = userAnswer === undefined || userAnswer === null
      const normalisedAnswer =
        hasNoUserAnswer && typeId === FORM_ITEM_TYPES.CHECKBOX
          ? false
          : userAnswer
      // -------------------------------------------------

      if (hasNoUserAnswer && typeId !== FORM_ITEM_TYPES.CHECKBOX) {
        // unchanged skip logic for non-checkboxes
        console.log(
          '🔄 mapFormAnswersObject: Skipping item - no user answer provided',
          {
            itemId: item.id,
            itemType: typeId,
            sectionKey,
          }
        )
        return
      }

      // Cast the value if needed (primarily for numeric fields)
      const castedValue = castNumericValue(normalisedAnswer, typeId)

      // Check if the final value is empty and should be omitted
      if (isEmptyValue(castedValue, typeId)) {
        console.log('🔄 mapFormAnswersObject: Skipping item - value is empty', {
          itemId: item.id,
          itemType: typeId,
          sectionKey,
          userAnswer: normalisedAnswer,
          castedValue,
        })
        return
      }

      // Initialize section object if it doesn't exist
      if (!nested[sectionKey]) {
        nested[sectionKey] = {}
      }

      // Include the non-empty value in the key-value payload
      nested[sectionKey][item.id] = castedValue

      console.log(
        '🔄 mapFormAnswersObject: Included item in key-value payload',
        {
          itemId: item.id,
          itemType: typeId,
          sectionKey,
          isNumeric: isNumericType(typeId),
          userAnswer: normalisedAnswer,
          finalValue: castedValue,
          finalType: typeof castedValue,
        }
      )
    })
  })

  console.log(
    '🔄 mapFormAnswersObject: Key-value payload created (empty values omitted)',
    {
      sectionCount: Object.keys(nested).length,
      nested,
    }
  )

  return { form: nested }
}
